import React, { useMemo } from 'react';
import { FeatureFlags, isValidSeverity, severityBackgroundColorOrUndefined } from '@fabric-ui/components';
import { FormatterType, generateInnerDefaultFormatter } from '@fabric-ui/components/Table';
import { emptyArray, uniqBy } from '@fabric-ui/utils';
import { Box } from '@mui/material';
import LineWidgetBasis from '../../../../components/Widgets/LineWidgetBasis/LineWidgetBasis';
import { ChartClickInteraction } from '../../../../components/Widgets/types';
import { useAvContext } from '../../../../context/AvContextProvider';
import { NO_VALUE } from '../../constants';
import { getDateFormatFromWidget } from '../../EditCustomDashboard/utils';
import { useCustomPalette } from '../../hooks';
import { useGetDisplayName } from '../../hooks/useGetDisplayName';
import { useGetFieldType } from '../../hooks/useGetFieldType';
import { LineCategoryWidgetTypes, LineLegendPosition, LineSubType } from '../../types/LineWidget.types';

interface LineWidgetProps {
  widget: LineCategoryWidgetTypes;
  data?: Record<string, any>[];
  isLoading?: boolean;
  clickInteractions?: ChartClickInteraction[];
  selected?: { name: string }[];
  onSelect?: (v: any) => void;
}
enum WidgetType {
  MultiDims,
  MultiMetrics,
  Normal,
  NotValid,
}
export const useGetLineWidgetData = ({
  data,
  widget,
}: {
  widget: LineCategoryWidgetTypes;
  data?: Record<string, any>[];
}): { lineData: any[]; series: any[]; xAxisKey?: string } => {
  const {
    select: { dims, metrics },
    projectionId: { name: projName },
  } = widget.requests[0];
  const customPalette = useCustomPalette(data);
  const getDisplayName = useGetDisplayName(projName);
  const getHeadCellType = useGetFieldType(projName);
  const headCellType = getHeadCellType({ entityFieldKey: dims[0].name });
  const widgetType: WidgetType =
    dims.length === 2 && metrics.length === 1
      ? WidgetType.MultiDims
      : dims.length === 1 && metrics.length > 1
        ? WidgetType.MultiMetrics
        : metrics.length === 1 && dims.length === 1
          ? WidgetType.Normal
          : WidgetType.NotValid;
  switch (widgetType) {
    case WidgetType.MultiDims: {
      const createSeries = (data, secondDimKey) => {
        const uniqueValues = uniqBy(
          data.map(item => ({
            value: Array.isArray(item[secondDimKey]) ? item[secondDimKey].join(',') : item[secondDimKey],
          })),
          'value'
        );
        return uniqueValues.map(({ value }, i) => ({
          name: value === null ? NO_VALUE : `${value}`,
          dataKey: value === null ? NO_VALUE : value,
          color: isValidSeverity(value) ? severityBackgroundColorOrUndefined(value) : customPalette[i],
          type: 'line',
        }));
      };

      const series = createSeries(data, dims[1].name);
      const transformData = (data, dims, metrics) =>
        Object.values(
          data.reduce((acc, item) => {
            const firstDimValue = item[dims[0].name]; // Group by the first dimension
            const secondDimValue = item[dims[1].name]; // Use the second dimension as keys
            const metricValue = item[metrics[0].name]; // Use the metric as the value

            acc[firstDimValue] = acc[firstDimValue]
              ? acc[firstDimValue]
              : {
                  name: generateInnerDefaultFormatter({
                    value: firstDimValue,
                    type: headCellType,
                    fieldName: dims[0].name,
                    shouldDisplayNull: true,
                  }),
                  originalValue: firstDimValue,
                };

            acc[firstDimValue][secondDimValue === null ? NO_VALUE : secondDimValue] = metricValue;
            return acc;
          }, {})
        );
      const lineData = transformData(data, dims, metrics);

      return { lineData, series, xAxisKey: 'name' };
    }
    case WidgetType.MultiMetrics:
    case WidgetType.Normal: {
      const xAxis0Values = [...new Set(metrics.map(metric => metric.name))];
      const series = uniqBy(
        xAxis0Values?.map((v, i) => ({
          name: getDisplayName(v) || NO_VALUE,
          dataKey: v || NO_VALUE,
          color: isValidSeverity(v) ? severityBackgroundColorOrUndefined(v) : customPalette[i],
          type: 'line',
        })),
        v => v.name
      );
      const dataRow = data?.map(row =>
        data
          ?.filter(v => v[dims[0]?.name] === row[dims[0]?.name])
          .reduce(
            (acc, v) => ({
              date: v[dims[0]?.name],
              ...acc,
              ...metrics.reduce((acc, metric) => ({ ...acc, [metric.alias || 0]: v[metric.alias || 0] }), {}),
            }),
            {}
          )
      );
      const lineData = uniqBy(dataRow, v => v.date);

      return { lineData, series };
    }
    default:
      return { lineData: [], series: [] };
  }
};

const renderLineWidget = ({
  widget,
  data,
  series,
  isDate,
  xAxisKey,
  featureFlags,
  measurements,
  showDataLabels = false,
  onSelect,
  clickInteractions,
  selected,
}: {
  widget: LineCategoryWidgetTypes;
  data;
  series;
  isDate;
  xAxisKey?: string;
  featureFlags;
  measurements;
  showDataLabels: boolean;
  clickInteractions?: ChartClickInteraction[];
  selected?: { name: string }[];
  onSelect?: (v: any) => void;
}) => ({
  [LineSubType.Spline]: () => (
    <LineWidgetBasis
      data={data}
      series={series}
      sx={{ flexDirection: flexDirectionMap[widget.definition.custom.legend.position], pb: 1, pt: 2 }}
      dateFormat={getDateFormatFromWidget(widget)}
      isDate={isDate}
      xAxisKey={xAxisKey}
      customLabels={widget.definition.custom.customLabels}
      labelFormatter={widget.definition.custom.labelFormatter}
      lineChartPosition={widget.definition.custom.chartPosition}
      showDataLabels={showDataLabels && !!widget.definition.custom.showDataLabels}
      featureFlags={featureFlags}
      measurements={measurements}
      onSelect={onSelect}
      clickInteractions={clickInteractions}
      selected={selected}
      className="draggableCancel"
    />
  ),
  [LineSubType.Line]: () => (
    <LineWidgetBasis
      data={data}
      series={series.map(serie => ({ ...serie, lineType: 'linear' }))}
      sx={{ flexDirection: flexDirectionMap[widget.definition.custom.legend.position], pb: 1, pt: 2 }}
      dateFormat={getDateFormatFromWidget(widget)}
      isDate={isDate}
      xAxisKey={xAxisKey}
      customLabels={widget.definition.custom.customLabels}
      labelFormatter={widget.definition.custom.labelFormatter}
      lineChartPosition={widget.definition.custom.chartPosition}
      showDataLabels={showDataLabels && !!widget.definition.custom.showDataLabels}
      featureFlags={featureFlags}
      measurements={measurements}
      onSelect={onSelect}
      clickInteractions={clickInteractions}
      selected={selected}
      className="draggableCancel"
    />
  ),
});

const flexDirectionMap = {
  [LineLegendPosition.Top]: 'column',
  [LineLegendPosition.Bottom]: 'column-reverse',
};

const LineWidget: React.FC<LineWidgetProps> = ({
  widget,
  data,
  isLoading,
  clickInteractions = emptyArray,
  selected = emptyArray,
  onSelect,
}) => {
  const { featureFlags, measurements } = useAvContext();
  const customPalette = useCustomPalette(data);
  const getType = useGetFieldType(widget.requests[0].projectionId.name);
  const { lineData, series, xAxisKey } = useGetLineWidgetData({ data, widget });

  const lineTypeWidget = useMemo(
    () =>
      renderLineWidget({
        widget,
        data: lineData,
        series,
        isDate: [FormatterType.historicDate, FormatterType.date].includes(
          getType({ entityFieldKey: widget.requests[0].select.dims[0].name })! as FormatterType
        ),
        xAxisKey,
        featureFlags,
        measurements,
        showDataLabels: Boolean(featureFlags?.[FeatureFlags.WidgetShowDataLabels] && widget.definition.custom.showDataLabels),
        onSelect,
        clickInteractions,
        selected,
      }),
    [data, customPalette, widget, isLoading]
  );

  return (
    <Box
      sx={{
        flex: 1,
        overflow: 'hidden',
        '.recharts-surface': {
          pointerEvents: 'auto',
          height: 'auto',
          width: 'auto',
          gap: 2,
          'text:not(.custom-chart-text)': {
            fontSize: '12px',
            fill: 'var(--color-content-base-secondary)',
          },
          '.recharts-xAxis': {
            transform: 'translate(0,5px)',
          },
        },
      }}>
      {lineTypeWidget[widget.type]()}
    </Box>
  );
};

export default LineWidget;
